// src/components/OptimizedImage.js
import React, { useState, useEffect, useRef } from 'react';
import { useIsClient } from '../utils/hydrationSafe';
import './OptimizedImage.css';

// Default placeholder (lightweight base64 SVG)
const DEFAULT_PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2YwZjBmMCIvPjwvc3ZnPg==';

function OptimizedImage({ 
  src, 
  alt = '', 
  width = 400, 
  height = 300, 
  className = '', 
  loading = 'lazy',
  fetchpriority = 'auto',
  priority = false,
  quality = 80, 
  blur = 0,
  placeholder = DEFAULT_PLACEHOLDER,
  intrinsicSize = true, // Whether to use width/height as intrinsic size
  webpSrc, // WebP version of the image
  avifSrc, // AVIF version of the image
  onLoad,
  onError,
  style = {}
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);
  const isClient = useIsClient();
  
  // Reset loading state when src changes to handle refresh properly
  useEffect(() => {
    if (src) {
      setLoaded(false);
      setError(false);
    }
  }, [src]);
  
  // Calculate aspect ratio for container
  const aspectRatio = width && height ? `${width} / ${height}` : 'auto';
  
  // Set style with aspect ratio
  const containerStyle = {
    aspectRatio,
    ...style
  };
  
  // For SSR, always render with placeholder showing initially
  useEffect(() => {
    if (!isClient || !imgRef.current) return;
    
    // Check if image is already cached
    if (imgRef.current.complete && imgRef.current.naturalHeight !== 0) {
      setLoaded(true);
    }
  }, [isClient, src]); // Added src dependency
  
  // Handle successful image load
  const handleLoad = (e) => {
    setLoaded(true);
    if (onLoad) onLoad(e);
  };
  
  // Handle image loading error
  const handleError = (e) => {
    setError(true);
    if (onError) onError(e);
    
    // Log error but don't crash the app
    console.warn(`Failed to load image: ${src}`);
  };

  // Image dimensions as attributes (important for CLS)
  const sizeAttrs = intrinsicSize ? { width, height } : {};
  
  // Combine CSS classes including loading state
  const imageClasses = `optimized-image ${className} ${loaded ? 'loaded' : 'loading'}`;
  
  return (
    <div 
      className="optimized-image-container"
      style={containerStyle}
      data-width={width}
      data-height={height}
    >
      {/* Show the placeholder until image loads */}
      {!loaded && (
        <img 
          src={placeholder}
          alt=""
          className="image-placeholder"
          width={width}
          height={height}
          aria-hidden="true"
        />
      )}
      
      {/* Use picture element for modern format support */}
      <picture>
        {/* AVIF - Best compression, modern browsers */}
        {avifSrc && (
          <source 
            srcSet={avifSrc} 
            type="image/avif" 
          />
        )}
        
        {/* WebP - Good compression, wide browser support */}
        {webpSrc && (
          <source 
            srcSet={webpSrc} 
            type="image/webp" 
          />
        )}
        
        {/* Fallback image */}
        <img
          ref={imgRef}
          src={error ? placeholder : src}
          alt={alt}
          loading={priority ? 'eager' : loading}
          fetchpriority={priority ? 'high' : fetchpriority}
          onLoad={handleLoad}
          onError={handleError}
          className={imageClasses}
          {...sizeAttrs}
        />
      </picture>
    </div>
  );
}

export default OptimizedImage;